// api
import type {
  GetDeviceDto,
  GetDeviceLocationDto,
  GetDeviceTagDto,
} from '@/api/generated/recalc/endpoints/v2/devices';

const WORD_BOUNDARY_REGEX = /([a-z0-9])([A-Z])/g;
const ACRONYM_BOUNDARY_REGEX = /([A-Z])([A-Z][a-z])/g;

export function formatDeviceLabel(value?: string | null) {
  if (!value) {
    return '';
  }

  return value
    .replace(ACRONYM_BOUNDARY_REGEX, '$1 $2')
    .replace(WORD_BOUNDARY_REGEX, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\bM Bus\b/gi, 'M-Bus')
    .trim();
}

export function formatDeviceTypeLabel(deviceType?: string | null) {
  return formatDeviceLabel(deviceType);
}

export function formatCapabilityLabel(capabilityName?: string | null) {
  return formatDeviceLabel(capabilityName);
}

export function getDeviceDisplayName(device?: GetDeviceDto | null) {
  return device?.displayName?.trim() || String(device?.deviceId ?? '');
}

export function formatDeviceLocation(location?: GetDeviceLocationDto | null) {
  if (!location) {
    return '';
  }

  if (location.address?.trim()) {
    return location.address.trim();
  }

  if (location.latitude != null && location.longitude != null) {
    return `${location.latitude.toFixed(5)}, ${location.longitude.toFixed(5)}`;
  }

  return '';
}

export function formatDeviceTag(tag?: GetDeviceTagDto | null) {
  if (!tag) {
    return '';
  }

  const key = tag.key?.trim();
  const value = tag.value?.trim();

  if (key && value) {
    return `${key}: ${value}`;
  }

  return key || value || '';
}

export function getDeviceCapabilityEntries(
  capabilities?: GetDeviceDto['capabilities'] | null,
) {
  return Object.entries(capabilities ?? {}).sort(([left], [right]) =>
    formatCapabilityLabel(left).localeCompare(formatCapabilityLabel(right)),
  );
}

/**
 * Extracts primitive values from an array for chip rendering.
 * Returns null if the array contains non-primitive values or is empty.
 * This allows components to render arrays as chips when appropriate.
 */
export function extractArrayChipItems(value: unknown): string[] | null {
  if (!Array.isArray(value) || value.length === 0) {
    return null;
  }

  const items = value.filter((item) => {
    const itemType = typeof item;
    return (
      item == null ||
      itemType === 'string' ||
      itemType === 'number' ||
      itemType === 'boolean'
    );
  });

  // Only return if all items were primitive
  if (items.length === value.length) {
    return items.map(String);
  }

  return null;
}

export function formatDeviceValue(value: unknown) {
  if (value == null) {
    return '';
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value) : '';
  }

  if (typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value)) {
    const chipItems = extractArrayChipItems(value);

    if (chipItems) {
      return chipItems.join(', ');
    }

    return JSON.stringify(value);
  }

  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }

  return String(value);
}
